import React, { FC, useCallback, useMemo, useState } from "react";
import { ButtonsGroup, IButtonItemProps, useDialogShadow } from "@evergis/ui";
import { CatalogResourceDc, CatalogResourceType } from "@evergis/api";
import { useTranslation } from "react-i18next";
import { useAttributes } from "../../../../../hooks/attributes/useAttributes";

import { useProjectMetaUpdate } from "../../../../../hooks/project/useProjectMetaUpdate";
import { useProjectSave } from "../../../../../hooks/project/useProjectSave";
import { useTrigger } from "../../../../../hooks/useTrigger";
import { DialogActions } from "../../../DialogComponents/DialogActions";
import { DialogContextProvider } from "../../../DialogComponents/FullScreenDialog";
import { CancelButton, RestrictedWidthContainer, StyledDialog } from "../../../DialogComponents/styled";
import { EditResource } from "../../../SelectFiles/Edit/EditResource";
import { useEditResource } from "../../../SelectFiles/Edit/useEditResource";
import { styledHeaderText } from "../../../styled";
import { useDialogInfo, useProjectInfoDialog } from "../../../../../hooks/useDialogs";

import { useProjectInfoByName } from "../../../../../hooks/project/useProjectByName";
import { ProjectInfoBlock } from "./Info/types";
import { StyledDialogContent, StyledDialogTitle } from "./styled";
import { ProjectSettingsMode } from "./types";
import { ProjectInfo } from "./Info/ProjectInfo";

// eslint-disable-next-line max-lines-per-function
export const ProjectSettings: FC = () => {
  const { t } = useTranslation();

  const {
    resources: [projectName],
    options: { saveMode = false } = {},
    closeDialog,
  } = useDialogInfo(true);

  const { shadows, setScrollContainer } = useDialogShadow();
  const [project, { loading }] = useProjectInfoByName(projectName);
  const { content } = project;

  const projectInfo = useMemo(
    () => (content?.projectInfo && Array.isArray(content?.projectInfo) ? content?.projectInfo : []),
    [content?.projectInfo],
  );

  const [blocks, updateBlocks] = useAttributes<ProjectInfoBlock>(projectInfo);

  const [path, setPath] = useState<CatalogResourceDc[]>([]);

  const item = useMemo(
    (): CatalogResourceDc => ({
      name: project?.alias || project?.name,
      systemName: project?.name,
      type: CatalogResourceType.Map,
      tags: project?.tags,
      description: project?.description,
      resourceId: project?.resourceId,
      parentId: project?.parentId,
    }),
    [project],
  );
  const { name, setName, description, setDescription, tags, setTags, systemName, setSystemName } =
    useEditResource(item);

  const parentId = useMemo(
    () => (path[path.length - 1] ? path[path.length - 1].resourceId : project?.parentId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [path[path.length - 1], project?.parentId],
  );
  const updatedProject = useMemo(
    (): CatalogResourceDc => ({
      name,
      systemName,
      type: CatalogResourceType.Map,
      tags,
      description,
      parentId,
    }),
    [name, systemName, tags, description, parentId],
  );
  const { update: onApply, projectUpdate, updating } = useProjectMetaUpdate(project, updatedProject, blocks);
  const [{ loading: saveLoading }, onSave] = useProjectSave(updatedProject, blocks);

  const modeOptions = useMemo(
    (): IButtonItemProps[] => [
      {
        value: ProjectSettingsMode.Properties,
        label: t("properties", { ns: "common" }),
      },
      {
        value: ProjectSettingsMode.Info,
        label: t("information", { ns: "common" }),
      },
    ],
    [t],
  );

  const [selectedMode, setSelectedMode] = useState<IButtonItemProps>(modeOptions[0]);

  const openProjectInfo = useProjectInfoDialog();

  const onInfoPreviewGenerate = useCallback(() => {
    openProjectInfo([""], { project: { ...project, content: { ...project.content, projectInfo: blocks } } });
  }, [blocks, project, openProjectInfo]);

  useTrigger(projectUpdate && !updating, closeDialog);

  return (
    <StyledDialog isOpen modal onCloseRequest={closeDialog} minWidth={"58rem"} maxWidth={"58rem"}>
      <DialogContextProvider
        dialogContext={{
          title: {
            text: styledHeaderText(
              t(saveMode ? "editResource.projectSave" : "editResource.projectProperties", { ns: "dialogs" }),
            ),
          },
          meta: {
            aliasLabel: t("editResource.projectName", { ns: "dialogs" }),
            descriptionLabel: t("editResource.projectDescription", { ns: "dialogs" }),
            loaderLabel: t("editResource.uploadImage", { ns: "dialogs" }),
          },
          applyText: t("actions.save", { ns: "common" }),
        }}
      >
        <StyledDialogTitle
          extraTitle={<ButtonsGroup options={modeOptions} selected={selectedMode} onClick={setSelectedMode} />}
          displayShadow={shadows.upper}
        />
        <StyledDialogContent ref={setScrollContainer}>
          <RestrictedWidthContainer>
            {selectedMode.value === ProjectSettingsMode.Properties && !loading && (
              <EditResource
                item={item}
                name={name}
                setName={setName}
                description={description}
                setDescription={setDescription}
                tags={tags}
                setTags={setTags}
                withSystemName={!!saveMode}
                systemName={systemName}
                setSystemName={saveMode ? setSystemName : void 0}
                withPath
                path={path}
                setPath={setPath}
              />
            )}
          </RestrictedWidthContainer>
          {selectedMode.value === ProjectSettingsMode.Info && <ProjectInfo {...updateBlocks} blocks={blocks} />}
        </StyledDialogContent>
        <DialogActions
          displayShadow={shadows.bottom}
          onApply={saveMode ? onSave : onApply}
          isWaiting={saveMode ? saveLoading : updating}
        >
          {selectedMode.value === ProjectSettingsMode.Info && (
            <CancelButton onClick={onInfoPreviewGenerate}>
              {t("panelHeader.previewAction", { ns: "common" })}
            </CancelButton>
          )}
        </DialogActions>
      </DialogContextProvider>
    </StyledDialog>
  );
};
